import * as React from 'react';
import { withRouter } from 'next/router';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { Card, CardContent } from '@material-ui/core';

import * as types from '../../../actions/actionTypes';
import { getClosingCosts } from '../../../actions/closingCosts';
import { buttonStyles } from '../../../styles/base';
import { isMobile } from '../../../lib/getDeviceSize';
import track from '../../../lib/track';
import combineStyles from '../../../styles/combineStyles';
import BackButton from '../../../components/utility/BackButton';

import ClosingCostsForm from '../../../components/pages/tools/closing-costs/ClosingCostsForm';
import ClosingCosts from '../../../components/pages/tools/closing-costs/ClosingCosts';
import PageBase from '../../../components/layout/PageBase';
import Content from '../../../components/pages/tools/content';
import ToolsHeader from '../../../components/pages/tools/header';

import style from '../../../styles/sass/pages/Tools.module.scss';

export interface Props {
  classes: any;
  closingCostState: string;
  dispatch: any;
  getClosingCosts: Function;
  prepaidInfo: any;
  router: any;
  token?: string;
  toriiBrokerage: any;
  traditionalBrokerage: any;
}
export interface State {
  closingCostState: string;
  loaded: boolean;
  percentDownpayment: number | undefined;
  price: number | undefined;
}

const styles = createStyles({
  card: {
    margin: '0 auto',
    padding: isMobile() ? '0 10px 15px' : 34,
    width: isMobile() ? (window.innerWidth < 350 ? '100%' : 350) : 640,
  },
  toolInfoContainer: {
    height: 'auto',
    padding: isMobile() ? 'auto' : 0,
    textAlign: 'left',
    width: isMobile() ? (window.innerWidth < 350 ? '100%' : 350) : 640,
  },
});

export class Calculator extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      closingCostState: this.props.closingCostState,
      loaded: false,
      percentDownpayment: undefined,
      price: undefined,
    };
  }

  handleSubmit = (values: any) => {
    if (!this.props.token) {
      this.props.dispatch({
        postAuthPath: this.props.router.pathname,
        type: types.OpenSignupDialog,
      });
    } else {
      track.toolUsed('Closing Costs Calculator', values);
      this.setState({
        closingCostState: values.location,
        loaded: true,
        percentDownpayment: values.percentDownpayment,
        price: values.price,
      });
      this.props.getClosingCosts(
        values.price,
        values.percentDownpayment,
        values.location,
        this.props.token,
      );
    }
  };

  renderContent = (needClosingCost: boolean) => {
    const {
      classes,
      traditionalBrokerage,
      toriiBrokerage,
      prepaidInfo,
    } = this.props;
    const { closingCostState, percentDownpayment, price } = this.state;
    return (
      <Card className={classes.card}>
        <CardContent>
          <ClosingCostsForm
            calculatorSubmit={this.handleSubmit}
            loaded={this.state.loaded}
            location={closingCostState}
            price={price}
            percentDownpayment={percentDownpayment}
          />

          {needClosingCost && (
            <ClosingCosts
              location={closingCostState}
              percentDownpayment={percentDownpayment}
              prepaidInfo={prepaidInfo}
              price={price}
              toriiBrokerage={toriiBrokerage}
              traditionalBrokerage={traditionalBrokerage}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  render() {
    const { classes, prepaidInfo, router } = this.props;
    return (
      <PageBase title="Estimate closing costs | Torii Closing Cost Calculator">
        <BackButton goBack={() => router.push('/tools')} />
        <div
          className={`${style.toolContainer} tool-container ${style.closingCosts} col-xs-12 center-xs`}
        >
          <ToolsHeader
            title="Closing Cost Calculator"
            slug="closing-costs"
            subTitle="Esimate closing costs on your home purchase"
          />
          <div className={`col-xs-12 ${style.toolContent} tool-content`}>
            {Object.keys(prepaidInfo).length > 0 ? (
              this.renderContent(true)
            ) : (
              <Content>{this.renderContent(false)}</Content>
            )}
            <div
              className={`col-xs-12 center-xs ${style.toolInfo} ${classes.toolInfoContainer}`}
            >
              <p className={[style.textStyle, style.bold].join(' ')}>
                What are closing costs?
              </p>
              <br />
              <p className={style.textStyle}>
                The term "closing costs" includes a variety of expenses outside
                of the home’s purchase price, such as fees for an appraisal,
                lender costs, and more.
              </p>
              <br />
              <p className={style.textStyle}>
                Closing costs are typically paid by the buyer at the closing. A
                seller is responsible for{' '}
                <a
                  href="https://blog.toriihomes.com/real-estate-commission/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  the agents’ commission, which is included in the selling price
                  of the home
                </a>
                .
              </p>
              <br />
              <p className={[style.textStyle, style.bold].join(' ')}>
                Other resources related to closing costs:
              </p>
              <br />
              <ul style={{ listStyle: 'disc', lineHeight: '24px', margin: 0 }}>
                <li className={style.textStyle}>
                  More about the Torii
                  <a
                    href="https://blog.toriihomes.com/torii-tools-closing-cost-calculator/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {' '}
                    Closing Cost Calculator
                  </a>
                </li>
                <li className={style.textStyle}>
                  <a
                    href="https://blog.toriihomes.com/closing-costs-101/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Closing Costs 101
                  </a>{' '}
                  (What are all of these fees?)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </PageBase>
    );
  }
}

const combinedStyles = combineStyles(buttonStyles, styles);

const mapStateToProps = (state: any) => ({
  closingCostState: state.closingCosts.closingCostState,
  prepaidInfo: state.closingCosts.prepaidInfo,
  token: state.auth.token,
  toriiBrokerage: state.closingCosts.toriiBrokerage,
  traditionalBrokerage: state.closingCosts.traditionalBrokerage,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
  getClosingCosts(
    homePrice: string,
    percentDown: number,
    location: string,
    token: string,
  ) {
    dispatch(getClosingCosts(homePrice, percentDown, location, token));
  },
});

export default withStyles(combinedStyles)(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(Calculator)),
);
